import React, { createContext, useState, useEffect } from 'react';
import { authService } from '../services/api';
import { syncAllPendingActions } from '../services/api';
import { setUser as setSentryUser, clearUser as clearSentryUser, addBreadcrumb } from '../config/sentry.config';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sync pending actions with MongoDB
  const syncPendingActions = async () => {
    try {
      console.log('Syncing pending actions after authentication...');
      await syncAllPendingActions();
    } catch (error) {
      console.error('Error syncing pending actions:', error);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Use the new initializeAuth function from auth service
        const isAuthenticated = await authService.initializeAuth();

        if (isAuthenticated) {
          const storedUser = authService.getStoredUser();
          if (storedUser) {
            setUser(storedUser);

            // Validate token and get fresh user data
            try {
              const userData = await authService.getCurrentUser();
              setUser(userData);

              // Set user context in Sentry for error tracking
              setSentryUser({
                id: userData._id || userData.id,
                email: userData.email,
                username: userData.username,
                role: userData.role,
              });

              // Add breadcrumb for authentication
              addBreadcrumb({
                category: 'auth',
                message: 'User authenticated from stored session',
                level: 'info',
                data: {
                  userId: userData._id || userData.id,
                  email: userData.email,
                  role: userData.role,
                },
              });

              // Sync any pending actions after successful authentication
              syncPendingActions();
            } catch (error) {
              console.error('Token validation failed:', error);
              // Clear all auth data if validation fails
              localStorage.removeItem('token');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('user');
              setUser(null);
              clearSentryUser();
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setError('Authentication failed');
        // Clear all auth data on error
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        setUser(null);
        clearSentryUser();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);
  
  const login = async (credentials) => {
    try {
      setLoading(true);
      const data = await authService.login(credentials);
      setUser(data.user);
      setError(null);
      
      // Set user context in Sentry for error tracking
      setSentryUser({
        id: data.user._id || data.user.id,
        email: data.user.email,
        username: data.user.username,
        role: data.user.role,
      });

      // Add breadcrumb for successful login
      addBreadcrumb({
        category: 'auth',
        message: 'User logged in successfully',
        level: 'info',
        data: {
          userId: data.user._id || data.user.id,
          email: data.user.email,
          role: data.user.role,
        },
      });
      
      // Sync any pending actions after successful login
      await syncPendingActions();
      
      return data;
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      setError(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  const logout = async () => {
    try {
      // Add breadcrumb before logout
      addBreadcrumb({
        category: 'auth',
        message: 'User logged out',
        level: 'info',
      });

      await authService.logout();
    } catch (error) {
      console.warn('Logout error:', error);
      // Clear local storage anyway
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
    setUser(null);
    
    // Clear user context from Sentry
    clearSentryUser();
  };
  
  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        loading, 
        error, 
        login, 
        logout, 
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin'
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
